import { z } from "zod";
import { SHIPMENT_STATUSES, type CarrierConnectionStatus, type CodState, type ShipmentStatus } from "../enums";
import { CARRIER_CODES, type CarrierCode } from "../carriers";
import { addressSchema } from "./common";

// Shipping: carrier connections, shipments, polling, smart carrier choice, public tracking.

const objectIdStr = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const minor = z.number().int("Montant invalide").min(0, "Montant invalide");
const isoDate = z.string().datetime({ offset: true, message: "Date invalide" });

/** Status tabs of Livraisons & suivi. Each tab groups normalized statuses; late and to_verify are computed. */
export const SHIPMENT_TABS = {
  pending: ["pending"],
  in_transit: ["picked_up", "in_transit", "out_for_delivery"],
  delivered: ["delivered"],
  issues: ["failed_attempt", "exception", "unknown"],
  returns: ["returning", "returned"],
  cancelled: ["cancelled"],
} as const satisfies Record<string, readonly ShipmentStatus[]>;

export const SHIPMENT_TAB_VALUES = ["all", "pending", "in_transit", "delivered", "issues", "returns", "cancelled", "late", "to_verify"] as const;
export type ShipmentTab = (typeof SHIPMENT_TAB_VALUES)[number];

export const SHIPMENT_TAB_LABELS: Record<ShipmentTab, string> = {
  all: "Toutes",
  pending: "En préparation",
  in_transit: "En transit",
  delivered: "Livrées",
  issues: "Incidents",
  returns: "Retours",
  cancelled: "Annulées",
  late: "En retard",
  to_verify: "À vérifier",
};

/** Statuses a shipment can be in while the parcel travels (late detection, polling). */
export const IN_FLIGHT_SHIPMENT_STATUSES: readonly ShipmentStatus[] = ["picked_up", "in_transit", "out_for_delivery", "failed_attempt"];

// ---------------------------------------------------------------------------------------------------------------
// Carrier connections

export const carrierCredentialsInput = z.record(z.string().min(1).max(64), z.string().max(4096));

export const carrierTariffInput = z.object({
  deliveryFeeMinor: minor.nullable().optional(),
  returnFeeMinor: minor.nullable().optional(),
  codFeeMinor: minor.nullable().optional(),
  codFeePercent: z.number().min(0, "Pourcentage invalide").max(100, "Pourcentage invalide").nullable().optional(),
  codFeeMinMinor: minor.nullable().optional(),
  settlementDays: z.number().int().min(0).max(120).nullable().optional(),
});
export type CarrierTariffInput = z.infer<typeof carrierTariffInput>;

export const carrierConnectionSettingsInput = z.object({
  pickupAddress: addressSchema.partial().optional(),
  defaultWeightGrams: z.number().int().min(10, "Poids minimum 10 g").max(100_000, "Poids maximum 100 kg").optional(),
  allowOpenParcel: z.boolean().optional(),
  fragileByDefault: z.boolean().optional(),
  exchangeAllowed: z.boolean().optional(),
  notes: z.string().trim().max(1000).optional(),
  tariff: carrierTariffInput.optional(),
});
export type CarrierConnectionSettingsInput = z.infer<typeof carrierConnectionSettingsInput>;

export const carrierConnectionCreateSchema = z.object({
  carrierCode: z.enum(CARRIER_CODES, { errorMap: () => ({ message: "Transporteur inconnu" }) }),
  label: z.string().trim().max(80).optional().default(""),
  marketCode: z.string().length(2).toUpperCase().optional().default("TN"),
  credentials: carrierCredentialsInput.optional().default({}),
  settings: carrierConnectionSettingsInput.optional(),
});
export type CarrierConnectionCreateInput = z.infer<typeof carrierConnectionCreateSchema>;

export const carrierConnectionUpdateSchema = z.object({
  label: z.string().trim().max(80).optional(),
  settings: carrierConnectionSettingsInput.optional(),
  options: z
    .object({
      pickupIndex: z.number().int().min(0).max(100_000).nullable().optional(),
      productCode: z.string().trim().regex(/^[A-Z0-9]{1,4}$/, "Code produit invalide").optional(),
      productType: z.string().trim().regex(/^[A-Z]{2,4}$/, "Type de produit invalide").optional(),
    })
    .optional(),
});
export type CarrierConnectionUpdateInput = z.infer<typeof carrierConnectionUpdateSchema>;

export const carrierCredentialsUpdateSchema = z.object({ credentials: carrierCredentialsInput });

export const shippingSettingsSchema = z.object({
  autoShip: z.boolean().optional(),
  autoShipMaxRisk: z.number().int().min(0, "Entre 0 et 100").max(100, "Entre 0 et 100").optional(),
  smartCarrierChoice: z.boolean().optional(),
  notifyCustomer: z.boolean().optional(),
  defaultCarrierConnectionId: objectIdStr.nullable().optional(),
});
export type ShippingSettingsInput = z.infer<typeof shippingSettingsSchema>;

export const localitySearchQuery = z.object({
  q: z.string().trim().max(80).optional(),
  governorate: z.string().trim().max(60).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

// ---------------------------------------------------------------------------------------------------------------
// Shipments

export const parcelOptionsSchema = z.object({
  weightGrams: z.number().int().min(10, "Poids minimum 10 g").max(100_000, "Poids maximum 100 kg").optional(),
  pieces: z.number().int().min(1).max(50).optional(),
  description: z.string().trim().max(200).optional(),
  openable: z.boolean().optional(),
  fragile: z.boolean().optional(),
});
export type ParcelOptionsInput = z.infer<typeof parcelOptionsSchema>;

export const createShipmentSchema = z.object({
  orderId: objectIdStr,
  carrierConnectionId: objectIdStr.nullable().optional(),
  localityId: objectIdStr.nullable().optional(),
  parcel: parcelOptionsSchema.optional(),
});
export type CreateShipmentInput = z.infer<typeof createShipmentSchema>;

export const bulkShipSchema = z.object({
  orderIds: z.array(objectIdStr).min(1, "Sélectionnez au moins une commande").max(100, "100 commandes maximum par envoi"),
  /** null or absent: smart choice or default carrier per order. */
  carrierConnectionId: objectIdStr.nullable().optional(),
  parcel: parcelOptionsSchema.omit({ description: true }).optional(),
  /** Locality chosen by the user per order (orderId to locality id). */
  localities: z.record(objectIdStr, objectIdStr).optional(),
});
export type BulkShipInput = z.infer<typeof bulkShipSchema>;

export const manualShipmentSchema = z.object({
  orderId: objectIdStr,
  carrierConnectionId: objectIdStr,
  trackingNumber: z.string().trim().min(3, "Numéro de suivi trop court").max(64).nullable().optional(),
  carrierLabel: z.string().trim().max(80).optional(),
  parcel: parcelOptionsSchema.optional(),
  note: z.string().trim().max(500).optional(),
});
export type ManualShipmentInput = z.infer<typeof manualShipmentSchema>;

export const shipPreviewSchema = z.object({
  orderIds: z.array(objectIdStr).min(1).max(100),
  carrierConnectionId: objectIdStr.nullable().optional(),
});

export const shipmentListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  status: z.enum([...SHIPMENT_TAB_VALUES, ...SHIPMENT_STATUSES]).default("all"),
  carrier: z.string().trim().max(40).optional(),
  governorate: z.string().trim().max(60).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  q: z.string().trim().max(120).optional(),
  sort: z.enum(["recent", "oldest", "updated"]).default("recent"),
});
export type ShipmentListQuery = z.infer<typeof shipmentListQuery>;

export const shipmentStatsQuery = z.object({ from: isoDate.optional(), to: isoDate.optional() });
export type ShipmentStatsQuery = z.infer<typeof shipmentStatsQuery>;

export const manualStatusSchema = z.object({
  status: z.enum(SHIPMENT_STATUSES).refine((s) => s !== "unknown", "Choisissez un statut"),
  note: z.string().trim().max(500).optional().default(""),
  at: isoDate.optional(),
});
export type ManualStatusInput = z.infer<typeof manualStatusSchema>;

export const trackingNumberSchema = z.object({
  trackingNumber: z.string().trim().min(3, "Numéro de suivi trop court").max(64, "Numéro de suivi trop long"),
});

export const resolveUnknownSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("tracking"), trackingNumber: z.string().trim().min(3, "Numéro de suivi trop court").max(64) }),
  z.object({ action: z.literal("retry") }),
  z.object({ action: z.literal("abandon"), note: z.string().trim().max(500).optional() }),
]);
export type ResolveUnknownInput = z.infer<typeof resolveUnknownSchema>;

export const cancelShipmentSchema = z.object({ confirmReturn: z.boolean().optional().default(false) });

export const shipmentIdsSchema = z.object({ ids: z.array(objectIdStr).min(1, "Sélectionnez au moins une expédition").max(200, "200 expéditions maximum") });

export const suggestQuery = z.object({ orderId: objectIdStr });

// ---------------------------------------------------------------------------------------------------------------
// DTOs returned by the API

export interface ShipmentSummaryDto {
  id: string;
  orderId: string;
  carrierCode: string;
  trackingNumber: string | null;
  status: ShipmentStatus;
  labelUrl: string | null;
  trackingUrl: string | null;
}

export interface ShipmentEventDto {
  status: ShipmentStatus;
  providerStatus: string;
  at: string;
  location: string;
  note: string;
  source: "provider" | "manual" | "system";
}

export type ShipmentCreateOutcome = "pending" | "ok" | "unknown" | "failed";

export interface ShipmentListItemDto {
  id: string;
  orderId: string;
  orderNumber: string;
  customerId: string | null;
  customerName: string;
  customerPhone: string;
  governorate: string;
  city: string;
  carrierCode: string;
  carrierName: string;
  carrierConnectionId: string | null;
  trackingNumber: string | null;
  status: ShipmentStatus;
  providerStatus: string;
  codAmountMinor: number;
  currency: string;
  codState: CodState;
  createOutcome: ShipmentCreateOutcome;
  late: boolean;
  lastEvent: { label: string; at: string } | null;
  expectedDeliveryAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LocalityDto {
  id: string;
  externalId: string;
  name: string;
  delegation: string;
  governorate: string;
  zip: string;
  score?: number;
}

export interface ShipmentDto extends ShipmentListItemDto {
  recipient: { name: string; phone: string; governorate: string; delegation: string; city: string; line1: string; zip: string; notes: string };
  parcel: { weightGrams: number; pieces: number; description: string; openable: boolean; fragile: boolean };
  events: ShipmentEventDto[];
  error: string | null;
  attempts: number;
  pickedUpAt: string | null;
  deliveredAt: string | null;
  returnedAt: string | null;
  cancelledAt: string | null;
  lastPolledAt: string | null;
  nextPollAt: string | null;
  pollFailures: number;
  pollStoppedAt: string | null;
  createdVia: "manual" | "auto" | "bulk" | "playbook";
  trackingUrl: string | null;
  carrierLabel: string;
  carrierFeeMinor: number | null;
  locality: { externalId: string; name: string; delegation: string; governorate: string } | null;
  pickup: { requestedAt: string | null; reference: string | null };
  cod: { state: CodState; collectedAt: string | null; settledAt: string | null };
  label: { carrier: boolean; generated: boolean };
  actions: {
    canCancel: boolean;
    cancelMode: "cancel" | "return" | "local" | null;
    canRefresh: boolean;
    canPickup: boolean;
    canSetTracking: boolean;
    canResolve: boolean;
  };
}

export interface ShipmentListResponse {
  items: ShipmentListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: Record<ShipmentTab, number>;
}

export interface ShipmentStatsDto {
  preparing: number;
  inTransit: number;
  deliveredInPeriod: number;
  late: number;
  returnedInPeriod: number;
  deliveryRate: number | null;
  toVerify: number;
  codInFlightMinor: number;
  currency: string;
  byCarrier: Array<{ carrierCode: string; name: string; delivered: number; returned: number; inFlight: number; rate: number | null }>;
}

export interface CarrierConnectionDto {
  id: string;
  carrierCode: CarrierCode;
  name: string;
  label: string;
  marketCode: string;
  status: CarrierConnectionStatus;
  verifiedAt: string | null;
  lastVerifyAt: string | null;
  lastError: string | null;
  isDefault: boolean;
  accountLabel: string;
  settings: {
    pickupAddress: { name: string; phone: string; governorate: string; delegation: string; city: string; line1: string; zip: string; notes: string };
    defaultWeightGrams: number;
    allowOpenParcel: boolean;
    fragileByDefault: boolean;
    exchangeAllowed: boolean;
    notes: string;
    tariff: { deliveryFeeMinor: number | null; returnFeeMinor: number | null; codFeeMinor: number | null; codFeePercent: number | null; codFeeMinMinor: number | null; settlementDays: number | null };
  };
  options: { pickupAddresses: Array<{ index: number; label: string }>; pickupIndex: number | null; productCode: string | null; productType: string | null };
  localitiesSyncedAt: string | null;
  localitiesCount: number;
  activeShipments: number;
  createdAt: string;
}

export interface ShippingSettingsDto {
  autoShip: boolean;
  autoShipMaxRisk: number;
  smartCarrierChoice: boolean;
  notifyCustomer: boolean;
  defaultCarrierConnectionId: string | null;
}

export interface CarrierCandidateDto {
  connectionId: string;
  carrierCode: string;
  name: string;
  eligible: boolean;
  reason: string | null;
  pDeliver: number;
  measured: boolean;
  sampleSize: number;
  feesMinor: number;
  expectedMarginMinor: number;
  explanation: string;
}

export interface CarrierSuggestionDto {
  orderId: string;
  currency: string;
  recommendedConnectionId: string | null;
  mode: "ranked" | "single" | "none";
  explanation: string;
  candidates: CarrierCandidateDto[];
}

export interface ShipPreviewItemDto {
  orderId: string;
  orderNumber: string;
  customerName: string;
  phone: string;
  governorate: string;
  city: string;
  codAmountMinor: number;
  currency: string;
  shippable: boolean;
  reason: string | null;
  existingShipmentId: string | null;
  locality: {
    required: boolean;
    status: "resolved" | "ambiguous" | "missing" | "not_required" | "catalog_empty";
    selected: LocalityDto | null;
    candidates: LocalityDto[];
  };
}

export interface BulkShipResultDto {
  results: Array<{
    orderId: string;
    orderNumber: string | null;
    ok: boolean;
    shipment: ShipmentSummaryDto | null;
    error: { code: string; message: string; details?: unknown } | null;
  }>;
}

export interface PublicTrackingDto {
  client: { name: string; logoUrl: string | null };
  orderNumber: string;
  carrier: { code: string; name: string; color: string; monogram: string };
  status: ShipmentStatus;
  statusLabel: string;
  trackingRef: string | null;
  expectedDeliveryAt: string | null;
  createdAt: string;
  deliveredAt: string | null;
  events: Array<{ status: ShipmentStatus; label: string; at: string; location: string }>;
}
